import * as React from "react";
import axios from "axios";

import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import OrgsTable from "../components/Tables/OrgsTable";
import OrgUsersTable from "../components/Tables/OrgUsersTable";
import Loading from "./Loading";
import Title from "../components/Layout/Title";
import MenuItem from "@mui/material/MenuItem";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import MuiAlert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import dayjs from "dayjs";
import { Formik } from "formik";
import * as yup from "yup";

import { Button } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";

export default function Orgs() {
  const [displayEdit, setDisplayEdit] = React.useState(false);
  const [err, setErr] = React.useState(null);
  const [orgId, setOrgId] = React.useState("");
  const [refresh, setRefresh] = React.useState(0);
  const [btnLoading, setBtnLoading] = React.useState(false);
  const [editButtonLock, setEditButtonLock] = React.useState(true);

  //list of orgs to check if name is unique
  const [allOrgs, setAllOrgs] = React.useState([]);

  //alert states:
  const [openAlert, setAlertOpen] = React.useState(false);
  const [message, setMessage] = React.useState("");
  const [alertColour, setAlertColour] = React.useState();

  const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
  });

  const handleAlertClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setAlertOpen(false);
  };

  function handleClickEdit() {
    setDisplayEdit(true);
  }

  function hideEdit() {
    setDisplayEdit(false);
  }

  const [loading, setLoading] = React.useState(true);
  const [formUnlocked, setFormUnlocked] = React.useState(false);
  const [pageData, setPageData] = React.useState({
    org: {},
    users: {},
    full_accounts: 0,
  });

  function toggleForm() {
    setFormUnlocked(!formUnlocked);
  }

  React.useEffect(() => {
    setLoading(true);
    setEditButtonLock(true);

    if (orgId) {
      const fetchData = async () => {
        const orgData = await axios.get(`/orgs/${orgId}`);
        const orgs = await axios.get("/orgs");

        setPageData(orgData.data);

        const orgArr = orgs.data.orgs.filter(
          (org) => orgData.data.org.client !== org.client
        );

        const orgNames = orgArr.map((org) => org.client.toLowerCase());

        setAllOrgs(orgNames);
      };
      fetchData()
        .then(() => {
          setLoading(false);
          setEditButtonLock(false);
        })
        .catch(() => {
          setErr("Something went wrong. Please try again");
          setEditButtonLock(true);
          setLoading(false);
        });
    }
  }, [orgId]);

  // Form validation:
  const userSchema = yup.object().shape({
    subscriptionType: yup.string().required("Required"),
    accountType: yup.string().required("Required"),

    tolerance: yup.number().min(0).max(100).required("Required"),
    remaining: yup.number().min(1).required("Required"),

    type: yup.string().required("Required"),
    organization: yup
      .string()
      .min(2, "Too Short!")
      .max(50, "Too Long!")
      .required("Required")
      .matches(
        /^[a-zA-Z0-9\s\-]+$/,
        "Organisation name must only include letters and numbers"
      )
      .transform((value) => value.toLowerCase())
      .notOneOf(
        allOrgs.map((org) => org),
        "Organisation already exists"
      ),
  });

  //coverts date value to yyyy/mm/dd before post
  function dateFormat(date) {
    const newDate =
      "" +
      dayjs(date).year() +
      "-" +
      ("0" + (dayjs(date).month() + 1)).substring(
        ("0" + (dayjs(date).month() + 1)).length - 2
      ) +
      "-" +
      ("0" + dayjs(date).date()).substring(
        ("0" + dayjs(date).date()).length - 2
      );
    return newDate;
  }

  const initialValues = {
    organization: pageData.org.client,
    subscriptionType: pageData.org.level,
    keyOwner: !pageData.users[0]
      ? "none"
      : pageData.users[0].firstName + " " + pageData.users[0].lastName,
    keyValue: pageData.org.keynum,
    accountType: pageData.org.account_type,
    endDate: pageData.org.expiry,
    subscriptions: pageData.org.subscriptions,
    tolerance: pageData.org.tolerance != null ? pageData.org.tolerance : 0,
    remaining: pageData.org.remaining,
    used: pageData.org.used,
    type: pageData.org.keyType,
  };

  const handleSubmit = (values) => {
    setBtnLoading(true);

    values.endDate = dateFormat(values.endDate);

    axios
      .patch(`/orgs/${pageData.org.id}`, {
        id: pageData.org.id,
        values: values,
      })
      .then((res) => {
        setBtnLoading(false);

        toggleForm();
        setAlertColour("success");
        setMessage(res.data);
        setAlertOpen(true);
        setRefresh(refresh + 1);
      })
      .catch((err) => {
        setBtnLoading(false);
        setAlertColour("error");
        setMessage(err.response.data);
        setAlertOpen(true);
      });
  };

  return (
    <>
      <Snackbar
        open={openAlert}
        autoHideDuration={6000}
        onClose={handleAlertClose}
      >
        {
          <Alert
            onClose={handleAlertClose}
            severity={alertColour}
            sx={{ width: "100%" }}
          >
            {message}
          </Alert>
        }
      </Snackbar>
      <Grid id="i" container spacing={3} direction="row">
        <Grid item id="j" xs>
          <Grid item id="k" sx={{ mb: 3 }}>
            <Paper
              id="l"
              sx={{
                p: 2,
                display: "flex",
                flexDirection: "column",
                height: "auto",
                justifyContent: "center",
                alignItems: "center",
                minWidth: 400,
              }}
            >
              <OrgsTable
                setOrgId={setOrgId}
                handleClickEdit={handleClickEdit}
                refresh={refresh}
                setRefresh={setRefresh}
                setAlertOpen={setAlertOpen}
                setAlertColour={setAlertColour}
                setMessage={setMessage}
              />
            </Paper>
          </Grid>
          {displayEdit && (
            <Grid item>
              <Paper
                sx={{
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  height: "auto",
                  overflow: "auto",
                }}
              >
                {loading ? (
                  <Loading />
                ) : (
                  <OrgUsersTable
                    orgId={orgId}
                    pageData={pageData}
                    setPageData={setPageData}
                  />
                )}
              </Paper>
            </Grid>
          )}
        </Grid>
        {displayEdit && (
          <Grid item xs={12} md={4} lg={3}>
            <Paper
              sx={{
                p: 2,
                display: "flex",
                flexDirection: "column",
                height: "auto",
              }}
            >
              <>
                <Button onClick={hideEdit} variant="contained" sx={{ my: 1 }}>
                  Hide
                </Button>
                {loading ? (
                  <Loading />
                ) : (
                  <>
                    <Title>Organization</Title>
                    <Button
                      variant="contained"
                      onClick={toggleForm}
                      disabled={editButtonLock}
                    >
                      {formUnlocked ? "Lock" : "Edit"}
                    </Button>

                    {err && <p>{err}</p>}

                    <Formik
                      onSubmit={handleSubmit}
                      initialValues={initialValues}
                      validationSchema={userSchema}
                      validateOnChange={false}
                      validateOnBlur={true}
                    >
                      {({
                        values,
                        errors,
                        touched,
                        resetForm,
                        handleChange,
                        handleSubmit,
                        handleBlur,
                        setFieldValue,

                        isValidating,
                        dirty,
                      }) => (
                        <form onSubmit={handleSubmit} onReset={resetForm}>
                          <Box
                            id="editForm"
                            sx={{
                              mt: 1,
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                            }}
                          >
                            <TextField
                              margin="normal"
                              fullWidth
                              id="organization"
                              label="Organization name"
                              name="organization"
                              variant="standard"
                              value={values.organization}
                              disabled={!formUnlocked}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={
                                !!touched.organization && !!errors.organization
                              }
                              helperText={
                                touched.organization && errors.organization
                              }
                            />

                            <TextField
                              select
                              margin="normal"
                              fullWidth
                              variant="standard"
                              name="subscriptionType"
                              value={values.subscriptionType}
                              disabled={!formUnlocked}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={
                                !!touched.subscriptionType &&
                                !!errors.subscriptionType
                              }
                              helperText={
                                touched.subscriptionType &&
                                errors.subscriptionType
                              }
                              label="Subscription Type"
                            >
                              <MenuItem key={"premium"} value="premium">
                                premium
                              </MenuItem>
                              <MenuItem key={"standard"} value="standard">
                                standard
                              </MenuItem>
                            </TextField>
                            <TextField
                              margin="normal"
                              fullWidth
                              id="keyowner"
                              label="Key owner"
                              name="KeyOwner"
                              variant="standard"
                              value={values.keyOwner}
                              disabled={true}
                              error={!!touched.KeyOwner && !!errors.KeyOwner}
                              helperText={touched.KeyOwner && errors.KeyOwner}
                            />
                            <TextField
                              select
                              fullWidth
                              margin="normal"
                              variant="standard"
                              name="accountType"
                              value={values.accountType}
                              disabled={!formUnlocked}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              label="Default Account Type"
                              error={
                                !!touched.accountType && !!errors.accountType
                              }
                              helperText={
                                touched.accountType && errors.accountType
                              }
                            >
                              <MenuItem key={"plus"} value="plus">
                                plus
                              </MenuItem>
                              <MenuItem key={"standard"} value="standard">
                                standard
                              </MenuItem>
                              <MenuItem key={"trial"} value="trial">
                                trial
                              </MenuItem>
                              <MenuItem key={"free"} value="free">
                                free
                              </MenuItem>
                            </TextField>

                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <DesktopDatePicker
                                label="Expiry date"
                                inputFormat="DD/MM/YYYY"
                                disablePast
                                variant="standard"
                                name="endDate"
                                value={values.endDate}
                                disabled={!formUnlocked}
                                error={!!touched.endDate && !!errors.endDate}
                                helperText={touched.endDate && errors.endDate}
                                renderInput={(params) => {
                                  return (
                                    <TextField
                                      {...params}
                                      fullWidth
                                      margin="normal"
                                      variant="standard"
                                      onKeyDown={(e) => e.preventDefault()}
                                    />
                                  );
                                }}
                                onChange={(newValue) => {
                                  setFieldValue("endDate", newValue.$d);
                                }}
                              />
                            </LocalizationProvider>

                            <TextField
                              margin="normal"
                              fullWidth
                              value={values.subscriptions}
                              name="subscriptions"
                              variant="standard"
                              disabled={true}
                              label="Subscriptions"
                            ></TextField>
                            <TextField
                              margin="normal"
                              fullWidth
                              id="keyvalue"
                              label="Key value"
                              name="keyValue"
                              variant="standard"
                              value={values.keyValue}
                              disabled={true}
                              error={!!touched.Keyvalue && !!errors.Keyvalue}
                              helperText={touched.Keyvalue && errors.Keyvalue}
                            />
                            <TextField
                              type="number"
                              InputProps={{ inputProps: { min: 0 } }}
                              margin="normal"
                              fullWidth
                              value={values.tolerance}
                              variant="standard"
                              disabled={!formUnlocked}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              label="Risk tolerance"
                              name="tolerance"
                              error={!!touched.tolerance && !!errors.tolerance}
                              helperText={touched.tolerance && errors.tolerance}
                            ></TextField>
                            <TextField
                              type="number"
                              InputProps={{ inputProps: { min: 0 } }}
                              margin="normal"
                              fullWidth
                              value={values.remaining}
                              variant="standard"
                              name="remaining"
                              disabled={!formUnlocked}
                              label="Remaining"
                              error={!!touched.remaining && !!errors.remaining}
                              helperText={touched.remaining && errors.remaining}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            ></TextField>
                            <TextField
                              type="number"
                              margin="normal"
                              fullWidth
                              label="Used"
                              name="used"
                              variant="standard"
                              value={values.used}
                              disabled={true}
                              // error={!!touched.used && !!errors.used}
                              // helperText={touched.used && errors.used}
                              // onChange={handleChange}
                              // onBlur={handleBlur}
                            />
                            <TextField
                              select
                              margin="normal"
                              fullWidth
                              name="type"
                              value={values.type}
                              variant="standard"
                              disabled={!formUnlocked}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={!!touched.type && !!errors.type}
                              helperText={touched.type && errors.type}
                              label="Key type"
                            >
                              <MenuItem key={"org"} value="org">
                                org
                              </MenuItem>
                              <MenuItem key={"cons"} value="cons">
                                cons
                              </MenuItem>
                            </TextField>
                            <LoadingButton
                              loading={btnLoading || isValidating}
                              type="submit"
                              variant="contained"
                              sx={{ mt: 3, mb: 2 }}
                              disabled={!formUnlocked || !dirty}
                            >
                              Update
                            </LoadingButton>
                            <Button type="reset" variant="contained" color="secondary">
                Reset
              </Button>
                          </Box>
                        </form>
                      )}
                    </Formik>
                  </>
                )}
              </>
            </Paper>
          </Grid>
        )}
      </Grid>
    </>
  );
}
